<template>
  <modal
    :active="active"
    @closed="$emit('closed', true)"
    :large="true"
    title="Dokumenter"
  >
    <div class="columns is-tablet flex-end mt-1 mr-1">
      <button class="button is-dark" @click="toggleMode">
        {{ mode === 'view' ? 'Vedhæft dokument' : 'Se vedhæftede dokumenter' }}
      </button>
    </div>
    <template v-if="mode === 'attach'">
      <div class="columns is-tablet">
        <form-field
          class="column"
          label="Dokumentgruppe"
          inner-container-class="select is-fullwidth"
          :error="error.document_group_id"
        >
          <input-select
            :options="listDocumentGroups"
            v-model="form.document_group_id"
            optionValueProp="id"
            optionTitleProp="name"
          />
        </form-field>
      </div>
      <div class="columns is-tablet">
        <form-field
          class="column"
          label="Sprog"
          inner-container-class="is-fullwidth"
          :error="error.locale_id"
        >
          <InputSelectSearch
            :options="listLocales"
            v-model="selectedLocales"
            label="name"
            :multiple="true"
            placeholder="Alle"
            :closeOnSelect="false"
          />
        </form-field>
      </div>
      <div class="columns is-tablet">
      <form-field
          class="column"
          label="Internt dokument"
          :error="error.is_internal"
      >
          <b-switch
              :rounded="false"
              v-model="form.is_internal"
              class="users-change__toggle"
          />
      </form-field>
      </div>
      <div class="columns is-tablet">
        <form-field
          class="column"
          label="Dokument"
          :error="error.files"
        >
          <template v-if="form.uploaded_file">
            <div>
              {{ `Filnavn: ${form.uploaded_file.name}` }}
            </div>
            <div class="is-flex gap-2 mt-1">
              <template v-if="selectedDocument">
                <button  class="button is-primary" @click="updateDocument">
                  Opdater dokument
                </button>
                <button class="button is-default" @click="reset">
                  Gå tilbage
                </button>
              </template>
              <template v-else>
                <button class="button is-primary" @click="createDocument">
                  Vedhæft dokument
                </button>
                <button class="button is-danger" @click="cancelUpload">
                  Annuller
                </button>
              </template>
            </div>
          </template>
          <InputDropzone
            v-else
            clickable
            :auto-process-queue="false"
            :url="'/api/documents/upload'"
            @success="fileAdded"
            @error="fileError"
          />
        </form-field>
        
      </div>
    </template>

    <template v-if="mode === 'view'">
      <List
        v-if="item.documents.length > 0"
        table-classes="table is-fullwidth"
        :columns="columns"
        :data="item.documents"
        :page-length="10"
        :rows-clickable="true"
        :default-order="{ col: 'document_group', dir: 'asc' }"
        @rowClicked="openDocument"
      >
        <template slot="column-actions" slot-scope="{ row }">
          <a class="button is-small" @click.stop="downloadDocument(row)" title="Download">
            <span class="icon">
              <i class="fas fa-download" />
            </span>
          </a>
          <a class="button is-small" @click.stop="copyUrl(row)" title="Kopier link">
            <span class="icon">
              <i class="fas fa-link" />
            </span>
          </a>
          <a class="button is-small" @click.stop="editDocument(row)" title="Rediger">
            <span class="icon">
              <i class="fas fa-edit" />
            </span>
          </a>
          <a class="button is-small" @click.stop="removeDocument(row)" title="Slet">
            <span class="icon">
              <i class="fas fa-trash" />
            </span>
          </a>
        </template>
      </List>
      <span v-else class="list-empty">Ingen dokumenter vedhæftet</span>
    </template>
    <template v-slot:footer>
      <div class="footer-spacer">
        <div>
          <button class="button is-default" @click="$emit('closed', true)">Luk</button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '@elems/modal'
import {FormField, InputSelect, InputSelectSearch, InputDropzone } from '@elems/form'
import {mapGetters} from "vuex";
import List from "@/elements/list";
import humanDate from "@/filters/humanDate";

export default {
  emits: ['updated'],
  filters: {
    humanDate,
  },
  components: {
    Modal,
    FormField,
    InputSelect,
    InputSelectSearch,
    InputDropzone,
    List,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mode: 'view',
      form: {
        all_locales: false,
        is_internal: false,
        locale_ids: [],
        document_group_id: null,
        uploaded_file: null,
      },
      selectedLocales: [],
      columns: [
        {
          name: 'document_group', 
          title: 'Dokumentgruppe', 
          sortable: true,
          searchable: true,
          width: 20,
          template: (row) => row.document_group.name,
          sort: (row) => row.document_group.name,
          search: (row) => row.document_group.name,
          hasTooltip: true
        },
        {
          name: 'is_internal', 
          title: 'Internt', 
          sortable: true,
          width: 10,
          template: (row) => row.is_internal ? 'Ja' : 'Nej',
        },
        {
          name: 'locale',
          title: 'Sprog',
          sortable: true,
          searchable: true,
          width: 15,
          template: (row) => row.all_locales ? 'Alle' : row.locales.map((item) => ufirst(item.code)).join(', '),
          sort: (row) => row.all_locales ? 'Alle' : row.locales.map((item) => ufirst(item.code)).join(', '),
          search: (row) => row.all_locales ? 'Alle' : row.locales.map((item) => item.name).join(', '),
          hasTooltip: true
        },
        {
          name: 'file_name',
          title: 'Filnavn',
          sortable: true,
          searchable: false,
          width: 27,
          hasTooltip: true
        },
        {
          name: 'created_at',
          title: 'Dato',
          sortable: true,
          width: '12',
          template: (row) => humanDate(row.created_at),
        },
        { name: 'actions', title: '', sortable: false, searchable: false, classes:() => 'has-text-right', width: 16 },
      ],
      error: [],
      selectedDocument: null,
    }
  },
  computed: {
    ...mapGetters([
      'listLocales',
      'listDocumentGroups',
    ]),
  },
  mounted() {
    this.reset();
  },
  watch: {
    active() {
      if (!this.active) {
        this.reset();
      }
    }
  },
  methods: {
    editDocument(itemDocument) {
      this.selectedDocument = itemDocument;
      this.mode = 'attach';
      this.form = {
        all_locales: itemDocument.all_locales,
        is_internal: itemDocument.is_internal,
        locale_ids: itemDocument.locales.map((item) => item.id),
        document_group_id: itemDocument.document_group.id,
        uploaded_file: {
          name: itemDocument.file_name,
        },
      };
      this.selectedLocales = itemDocument.locales;
    },
    toggleMode() {
      this.mode = this.mode === 'view' ? 'attach' : 'view';
    },
    reset() {
      this.form = {
        all_locales: false,
        is_internal: false,
        locale_ids: [],
        document_group_id: null,
        uploaded_file: null,
      };
      this.selectedDocument = null;
      this.selectedLocales = [];
      this.error = [];
      this.mode = 'view';
    }, 
    fileAdded(file, response) {
      this.form.uploaded_file = response;
    },
    fileError(file, error) {
      this.error = error.errors;
    },
    updateDocument() {
      this.form.locale_ids = this.selectedLocales.map((item) => item.id);
      this.form.all_locales = this.selectedLocales.length === 0;

      window.axios.put('/api/items/' + this.item.id + '/documents/' + this.selectedDocument.id, this.form)
        .then(({ data }) => {
          this.$store.dispatch('setSuccess', 'Dokumentet blev opdateret');
          this.$emit('updated');
          this.reset();
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.error = error.response.data.errors
          }
        })
    },
    createDocument() {
      this.form.locale_ids = this.selectedLocales.map((item) => item.id);
      this.form.all_locales = this.selectedLocales.length === 0;

      window.axios.post('/api/items/' + this.item.id + '/documents', this.form)
        .then(({ data }) => {
          this.$store.dispatch('setSuccess', 'Dokumentet blev vedhæftet');
          this.$emit('updated');
          this.reset();
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.error = error.response.data.errors
          }
        })
    },
    removeDocument(itemDocument) {
      if (!confirm('Er du sikker på du vil fjerne dokumentet?')) {
        return;
      }

      window.axios.delete('/api/items/' + this.item.id + '/documents/' + itemDocument.id)
        .then(({ data }) => {
          this.$store.dispatch('setSuccess', 'Dokumentet blev fjernet');
          this.$emit('updated');
        })
        .catch(error => {
          console.log(error);
        })
    },
    downloadDocument(itemDocument) {
      window.axios.get('/api/items/' + this.item.id + '/documents/' + itemDocument.id + '/download', {
        responseType: 'blob',
      })
        .then((data) => {
          const href = URL.createObjectURL(data.data);
          const a = document.createElement('a');
          a.href = href;
          a.download = itemDocument.file_name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(href);
        })
        .catch(error => {
          console.log(error);
        })
    },
    copyUrl(itemDocument) {
      navigator.clipboard.writeText(itemDocument.url)
        .then(() => {
          this.$store.dispatch('setSuccess', 'Link kopieret til udklipsholder');
        })
        .catch(error => {
          console.log(error);
        })

    },
    openDocument(itemDocument) {
      window.open(itemDocument.url, '_blank');
    },
    cancelUpload() {
      window.axios.delete('/api/documents/cancel-upload', {
        data: {
          path: this.form.uploaded_file.path,
        }
      })
        .then(() => {
          this.form.uploaded_file = null;
        })
        .catch(error => {
          console.log(error);
        })
    }
  }
}
</script>
